import { isNotNil } from "es-toolkit";
import type express from "express";
import * as v from "valibot";

export const validations = {
    deletable: v.optional(
        v.pipe(
            v.boolean(),
            v.value(true as const),
            v.transform(() => 1 as const),
        ),
    ),
};

export const BaseRecordSchema = v.object({
    page: v.pipe(v.number(), v.integer(), v.minValue(0)),
    rows: v.pipe(v.number(), v.integer(), v.minValue(1)),
    search: v.optional(v.pipe(v.string(), v.trim())),
});

export function defineRecordSchema<T extends v.ObjectEntries>(params: { filters: v.ObjectSchema<T, undefined> }) {
    return v.object({
        pagination: v.object({
            page: v.pipe(v.number(), v.integer(), v.minValue(0)),
            rows: v.pipe(v.number(), v.integer(), v.minValue(1)),
        }),
        search: v.optional(v.pipe(v.string(), v.trim())),
        filters: params.filters,
    });
}

export class SchemaObject<S extends v.BaseSchema<unknown, unknown, any>> {
    public schema!: S;

    public parse<T>(input: T extends express.Request ? never : T) {
        return v.parse(this.schema, input);
    }

    public parseStrict(input: v.InferInput<S>) {
        return v.parse(this.schema, input);
    }

    public get Input(): v.InferInput<S> {
        throw new Error("Input can't be used as a value");
    }

    public get Output(): v.InferOutput<typeof this.schema> {
        throw new Error("Output can't be used as a value");
    }
}

export function defineValidation<S extends v.BaseSchema<unknown, unknown, any>>(schema: S) {
    const schemaObject = class extends SchemaObject<S> {
        public override schema = schema;

        public static get Input(): v.InferInput<typeof schema> {
            throw new Error("Input can't be used as a value");
        }

        public static get Output(): v.InferOutput<typeof schema> {
            throw new Error("Output can't be used as a value");
        }
    };

    return new schemaObject();
}

export const baseVals = {
    string: (name?: string, maxLength?: number) => {
        const msg = isNotNil(name) ? `${name} tidak boleh kosong.` : "Tidak boleh kosong.";
        const pipe = v.pipe(v.string(), v.trim(), v.minLength(1, msg));
        if (isNotNil(maxLength) && maxLength > 0) {
            const maxLengthMsg = isNotNil(name)
                ? `${name} tidak boleh lebih dari ${maxLength} karekter.`
                : `Tidak boleh lebih dari ${maxLength} karakter.`;

            return v.nonNullish(v.pipe(pipe, v.maxLength(maxLength, maxLengthMsg)), msg);
        }

        return v.nonNullish(pipe, msg);
    },
    stringEmptiable: () => {
        return v.pipe(v.string(), v.trim());
    },
    email: (name = "Email") => {
        return v.pipe(baseVals.string(), v.email(`Format ${name} salah.`));
    },
    phone: (name = "No. Telepon") => {
        return v.pipe(
            baseVals.string(),
            v.transform((p) => p.replace(/^[^+0-9]+/g, "").replace(/[^0-9]+$/g, "")),
            v.regex(/^\+62\d+/, `Format ${name} salah, gunakan kode area +62 di awal.`),
            v.regex(/^\+62\d{9,13}/, `Format ${name} salah, panjang tidak sesuai.`),
            v.regex(/^\+62\d{9,13}$/, `Format ${name} tidak sesuai, gunakan format +628XXXXXXXXX.`),
        );
    },
    phoneNumberOnly: (name = "No. Telepon") => {
        return v.pipe(
            baseVals.string(),
            v.transform((p) => p.replace(/^[^+0-9]+/g, "").replace(/[^0-9]+$/g, "")),
            v.regex(/^62\d+/, `Format ${name} salah, gunakan kode area 62 di awal.`),
            v.regex(/^62\d{9,13}/, `Format ${name} salah, panjang tidak sesuai.`),
            v.regex(/^62\d{9,13}$/, `Format ${name} tidak sesuai, gunakan format 628XXXXXXXXX.`),
            v.transform((s) => `+${s}`),
        );
    },
    coordinate: (name = "Koordinat") => {
        return v.message(
            v.tuple([
                v.pipe(v.number(), v.minValue(-180), v.maxValue(180)), // lng
                v.pipe(v.number(), v.minValue(-90), v.maxValue(90)), // lat
            ]),
            `Format ${name} tidak sesuai.`,
        );
    },
    username: (name = "Nama Pengguna") => {
        return v.pipe(
            baseVals.string(),
            v.regex(
                /^[a-zA-Z0-9][a-zA-Z0-9_]{3,29}$/,
                `${name} harus memiliki panjang antara 4-30 karakter dan terdiri dari huruf atau angka.`,
            ),
        );
    },
    password: (name = "Kata Sandi") => {
        return v.pipe(baseVals.string(), v.minLength(6, `${name} harus memiliki panjang minimal 6 karakter.`));
    },
    identityNumber: (name = "No. Identitas") => {
        return v.nonNullish(
            v.pipe(
                baseVals.string(),
                v.regex(/^\d+$/, `Format ${name} salah, terdapat karakter bukan angka.`),
                v.regex(/^\d{16}$/, `Format ${name} salah, panjang tidak sesuai.`),
            ),
            `${name} tidak boleh kosong.`,
        );
    },
    ipv4: (name = "Alamat IP") => {
        return v.pipe(baseVals.string(), v.ipv4(`Format ${name} salah.`));
    },
    intRange: (min: number, max: number, name = "Nilai") => {
        return v.pipe(
            v.number(),
            v.integer(),
            v.minValue(min, `${name} tidak boleh kurang dari ${min}`),
            v.maxValue(max, `${name} tidak boleh lebih dari ${max}`),
        );
    },
    intMin: (min: number, name = "Nilai", opts?: { unit?: string }) => {
        const unitText = opts?.unit !== undefined ? ` ${opts.unit}` : "";
        return v.pipe(v.number(), v.integer(), v.minValue(min, `${name} tidak boleh kurang dari ${min}${unitText}`));
    },
    isoDate: (name = "Tanggal") => {
        return v.pipe(baseVals.string(), v.isoDate(`Format ${name} salah.`));
    },
    isoTimestamp: (name = "Timestamp") => {
        return v.pipe(baseVals.string(), v.isoTimestamp(`Format ${name} salah.`));
    },
    date: (name = "Tanggal") => {
        return v.nonNullish(v.date(`${name} tidak sesuai.`), `${name} tidak boleh kosong`);
    },
    file: (name = "Berkas") => {
        return v.pipe(
            baseVals.string(),
            v.check((input) => input.startsWith("data:"), `${name} tidak sesuai.`),
        );
    },
    binaryBoolean: (name: string) => {
        return v.nonNullish(
            v.pipe(
                v.number(`Format ${name} tidak sesuai`),
                v.picklist([1, 0], `Format ${name} tidak sesuai`),
                v.transform((s) => s === 1),
            ),
            `${name} tidak boleh kosong.`,
        );
    },
    pin: () => {
        return v.nonNullish(
            v.pipe(v.string(), v.digits("Format PIN salah."), v.length(6, "Format PIN salah, Panjang tidak sesuai.")),
            "PIN tidak boleh kosong",
        );
    },
};

export const listVals = {
    stringList: (name = "Nilai") => {
        return v.pipe(v.array(baseVals.string()), v.minLength(1, `${name} tidak boleh kosong`));
    },
    stringListEmptiable: () => {
        return v.array(baseVals.string());
    },
};
